// useModalSize — 모달 크기 커스텀(계정별 저장)의 프론트 진입점.
//
// 핵심 동작 / 안전 원칙:
//  - resizeKey 가 없으면(=대부분의 호출, 옵트인 안 한 모달) 아무 것도 하지 않는다. enabled=false,
//    custom=null 만 돌려주고 네트워크도 안 탄다 → 기본 동작에 영향 0.
//  - resizeKey 가 있으면 "내 모달 크기 전체"를 앱에서 단 한 번 lazy-load 해 모듈 캐시에 담는다.
//    이후 모든 모달은 메모리에서 즉시 자기 크기를 읽는다(모달 열 때마다 서버 호출 안 함).
//  - 저장/초기화는 캐시를 낙관적으로 바꾸고 서버에 비동기로 보낸다. 실패해도 화면은 안 깨지고
//    다음 새로고침 때 서버 진실로 수렴한다.
//  - 비로그인/조회 실패는 빈 캐시로 흡수 → 로그인 안 한 화면도 그대로 동작.

import { useEffect, useSyncExternalStore } from "react";
import {
  deleteModalPref,
  fetchAllModalPrefs,
  saveModalPref,
  type ModalSizePref,
} from "../api/modalPrefs";

// ── 모듈 전역 캐시 (앱 전체 1벌) ───────────────────────────────
let cache: Record<string, ModalSizePref> = {};
let loaded = false;
let loadPromise: Promise<void> | null = null;
let version = 0;
const listeners = new Set<() => void>();

function emit(): void {
  version += 1;
  listeners.forEach((l) => l());
}

/** 앱에서 처음 필요할 때 단 한 번 전체 설정을 불러온다. 실패는 빈 캐시로 흡수. */
function ensureLoaded(): Promise<void> {
  if (!loadPromise) {
    loadPromise = fetchAllModalPrefs()
      .then((prefs) => {
        cache = prefs;
      })
      .catch(() => {
        cache = {}; // 비로그인·네트워크 실패 — 커스텀 없음으로 간주(기본 크기).
      })
      .finally(() => {
        loaded = true;
        emit();
      });
  }
  return loadPromise;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
function getVersion(): number {
  return version;
}

const NOOP = () => {};

export interface UseModalSizeResult {
  /** resizeKey 가 주어져 리사이즈 기능이 켜졌는가. false 면 ModalShell 은 기존대로만 동작. */
  enabled: boolean;
  /** 내가 저장한 커스텀 크기. 한 번도 안 늘렸으면 null(=기본 프리셋 크기). */
  custom: ModalSizePref | null;
  /** 드래그로 크기를 바꿨을 때 호출 — 캐시 갱신 + 서버 저장. */
  save: (dim: ModalSizePref) => void;
  /** 원래대로 — 커스텀을 지우고 기본 크기로. */
  reset: () => void;
}

export function useModalSize(resizeKey?: string): UseModalSizeResult {
  // 캐시 변화를 구독해 저장/초기화 시 다시 그려지게 한다. (resizeKey 유무와 무관하게 항상 호출 — 훅 규칙)
  useSyncExternalStore(subscribe, getVersion, getVersion);

  useEffect(() => {
    if (resizeKey) void ensureLoaded();
  }, [resizeKey]);

  if (!resizeKey) {
    return { enabled: false, custom: null, save: NOOP, reset: NOOP };
  }

  const custom = loaded ? (cache[resizeKey] ?? null) : null;

  const save = (dim: ModalSizePref) => {
    const next: ModalSizePref = {};
    if (typeof dim.width === "number") next.width = Math.round(dim.width);
    if (typeof dim.height === "number") next.height = Math.round(dim.height);
    cache = { ...cache, [resizeKey]: next };
    emit();
    void saveModalPref(resizeKey, next).catch(() => {});
  };

  const reset = () => {
    if (!(resizeKey in cache)) return;
    const rest = { ...cache };
    delete rest[resizeKey];
    cache = rest;
    emit();
    void deleteModalPref(resizeKey).catch(() => {});
  };

  return { enabled: true, custom, save, reset };
}
